// Row for speaker / central-bank events.

import { useState } from "react";
import type { SpeakerResponse } from "../../types";
import { Flag } from "../Flag";
import { ImportanceIcon } from "../ImportanceIcon";
import { Markdown } from "../Markdown";
import { TimeCell } from "../TimeCell";
import {
  computeRowClasses,
  getCategory,
  getImportance,
  hasDetail,
  useChangeAnimation,
  type CommonRowProps,
} from "../rowCommon";

export function SpeakerRow(props: CommonRowProps) {
  useChangeAnimation(props);
  const [expanded, setExpanded] = useState(false);

  const event = props.event as SpeakerResponse;
  const category = getCategory(props.event);
  const detail = event.detail;
  const location = category?.location || event.location || "";
  const previousArticle = event.previous_article;

  const detailSpan =
    (props.showLowHigh ? 8 : 6) + (props.chart === "mini" ? 1 : 0);

  return (
    <>
      <div className={computeRowClasses(props) + " calendar-grid-row"}>
        <div className="cell-location">
          <span className="flag-container">
            <Flag region={location} />
          </span>
        </div>
        <div className="cell-time">
          <TimeCell event={props.event} />
        </div>
        <div className="cell-title">
          {hasDetail(props.event) && (
            <button onClick={() => setExpanded((v) => !v)} className="expand-toggle">
              {expanded ? "▼" : "▶"}
            </button>
          )}
          <div className="info">
            <span className="event-name">{event.summary}</span>
            {event.has_transcript && <span className="speaker-badge">Transcript</span>}
            {event.has_q_and_a && <span className="speaker-badge">Q & A</span>}
            {event.has_slides && <span className="speaker-badge">Slides</span>}
          </div>
        </div>
        <div className="cell-actual">
          {event.link ? (
            <a href={event.link} target="_blank" rel="noopener noreferrer" className="event-link">
              Link
            </a>
          ) : (
            "-"
          )}
        </div>
        {props.chart === "mini" && <div className="cell-chart">&nbsp;</div>}
        <div className="cell-expected">-</div>
        {props.showLowHigh && (
          <>
            <div className="cell-low">-</div>
            <div className="cell-high">-</div>
          </>
        )}
        <div className="cell-previous">
          {previousArticle ? (
            <a
              href={previousArticle}
              target="_blank"
              rel="noopener noreferrer"
              className="event-link"
            >
              <svg
                className="link-icon"
                width="12"
                height="12"
                viewBox="0 0 128 128"
                aria-hidden="true"
              >
                <path d="m 21.858963,17.826329 c -5.26487,0 -7.863637,2.65438 -7.863637,7.930848 l 0,84.416493 100.009344,0 0,-84.416493 c 0,-5.276468 -2.59876,-7.930848 -7.86363,-7.930848 l -84.282077,0 z m 5.309637,10.552062 73.6628,0 c 1.55968,0 2.68842,1.058105 2.68842,2.621213 l 0,65.933582 c 0,1.563108 -1.12875,2.68842 -2.68842,2.68842 l -73.6628,0 c -1.55967,0 -2.688424,-1.125321 -2.688424,-2.68842 l 0,-65.933582 c 0,-1.563108 1.128754,-2.621213 2.688424,-2.621213 z m -0.336054,6.721057 0,12.904432 74.334904,0 0,-12.904432 -74.334904,0 z m 32.865977,19.625491 0,16.802646 -16.735435,0 0,8.602955 16.735435,0 0,16.802646 8.602954,0 0,-16.802646 16.735435,0 0,-8.602955 -16.735435,0 0,-16.802646 -8.602954,0 z" />
              </svg>
              Speech
            </a>
          ) : (
            "-"
          )}
        </div>
        <div className="cell-revised">-</div>
        <div className="cell-importance">
          <ImportanceIcon importance={getImportance(props.event)} size="md" />
        </div>
      </div>
      {expanded && detail && (
        <div className="detail-row calendar-grid-row">
          <div className="cell-location">&nbsp;</div>
          <div className="cell-time">&nbsp;</div>
          <div className="cell-title" style={{ gridColumn: `span ${detailSpan}` }}>
            <div className="detail-content">
              <Markdown content={detail} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SpeakerRow;
